/**
 * Parlays Analytics Grid
 * Renders all parlay-level performance breakdown tables for parlays mode.
 */
import type { ReactElement } from 'react';
import type { ParlayDashboardData } from '../hooks/labDashboardTypes';
import { PerformanceTable } from './PerformanceTable';

interface ParlaysAnalyticsGridProps {
  data: ParlayDashboardData;
}

export function ParlaysAnalyticsGrid({ data }: ParlaysAnalyticsGridProps): ReactElement {
  return (
    <>
      {/* Tier Performance */}
      {data.tierPerformance.length > 0 && (
        <PerformanceTable
          title="By Tier"
          data={data.tierPerformance}
          columns={[
            { key: 'tier', label: 'Tier' },
            { key: 'wins', label: 'W', width: 'w-12' },
            { key: 'losses', label: 'L', width: 'w-12' },
            { key: 'winRate', label: 'Win %', format: 'percent', width: 'w-16' },
            { key: 'units', label: 'Units', format: 'units', width: 'w-16' },
          ]}
        />
      )}

      {/* Leg Count Performance */}
      {data.legCountPerformance.length > 0 && (
        <PerformanceTable
          title="By Leg Count"
          data={data.legCountPerformance.map((p) => ({
            ...p,
            legCountLabel: `${p.legCount} legs`,
          }))}
          columns={[
            { key: 'legCountLabel', label: 'Legs' },
            { key: 'wins', label: 'W', width: 'w-12' },
            { key: 'losses', label: 'L', width: 'w-12' },
            { key: 'winRate', label: 'Win %', format: 'percent', width: 'w-16' },
            { key: 'units', label: 'Units', format: 'units', width: 'w-16' },
          ]}
        />
      )}

      {/* Confidence Performance */}
      {data.confidencePerformance?.length > 0 && (
        <PerformanceTable
          title="By Confidence"
          data={data.confidencePerformance}
          columns={[
            { key: 'confidence', label: 'Confidence' },
            { key: 'wins', label: 'W', width: 'w-12' },
            { key: 'losses', label: 'L', width: 'w-12' },
            { key: 'winRate', label: 'Win %', format: 'percent', width: 'w-16' },
            { key: 'units', label: 'Units', format: 'units', width: 'w-16' },
          ]}
        />
      )}

      {/* SGP vs Regular Performance */}
      {data.sgpPerformance?.length > 0 && (
        <PerformanceTable
          title="By Parlay Type"
          data={data.sgpPerformance}
          columns={[
            { key: 'label', label: 'Type' },
            { key: 'wins', label: 'W', width: 'w-12' },
            { key: 'losses', label: 'L', width: 'w-12' },
            { key: 'winRate', label: 'Win %', format: 'percent', width: 'w-16' },
            { key: 'units', label: 'Units', format: 'units', width: 'w-16' },
          ]}
        />
      )}

      {/* Odds Range Performance */}
      {data.oddsRangePerformance?.length > 0 && (
        <PerformanceTable
          title="By Odds Range"
          data={data.oddsRangePerformance}
          columns={[
            { key: 'oddsRange', label: 'Odds' },
            { key: 'wins', label: 'W', width: 'w-12' },
            { key: 'losses', label: 'L', width: 'w-12' },
            { key: 'winRate', label: 'Win %', format: 'percent', width: 'w-16' },
            { key: 'units', label: 'Units', format: 'units', width: 'w-16' },
          ]}
        />
      )}

      {/* Origin Performance */}
      {data.sourcePerformance?.length > 0 && (
        <PerformanceTable
          title="By Origin"
          data={data.sourcePerformance}
          columns={[
            { key: 'origin', label: 'Origin' },
            { key: 'wins', label: 'W', width: 'w-12' },
            { key: 'losses', label: 'L', width: 'w-12' },
            { key: 'winRate', label: 'Win %', format: 'percent', width: 'w-16' },
            { key: 'units', label: 'Units', format: 'units', width: 'w-16' },
          ]}
        />
      )}

      {/* Day of Week Performance */}
      {data.dayOfWeekPerformance?.length > 0 && (
        <PerformanceTable
          title="By Day of Week"
          data={data.dayOfWeekPerformance}
          columns={[
            { key: 'dayName', label: 'Day' },
            { key: 'wins', label: 'W', width: 'w-12' },
            { key: 'losses', label: 'L', width: 'w-12' },
            { key: 'winRate', label: 'Win %', format: 'percent', width: 'w-16' },
            { key: 'units', label: 'Units', format: 'units', width: 'w-16' },
          ]}
        />
      )}

      {/* Sportsbook Performance */}
      {data.sportsbookPerformance?.length > 0 && (
        <PerformanceTable
          title="By Sportsbook"
          data={data.sportsbookPerformance}
          columns={[
            { key: 'sportsbookDisplay', label: 'Sportsbook' },
            { key: 'wins', label: 'W', width: 'w-12' },
            { key: 'losses', label: 'L', width: 'w-12' },
            { key: 'winRate', label: 'Win %', format: 'percent', width: 'w-16' },
            { key: 'units', label: 'Units', format: 'units', width: 'w-16' },
          ]}
        />
      )}
    </>
  );
}
